import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";

import { getIterableEmailType } from "generated/api/iterable-email-type";
import { getAdmin } from "generated/api/admin";
import type { IterableEmailSenderDetailRequest, IterableEmailTypeDTO } from "generated/schemas";

const emailTypeApi = getIterableEmailType();
const adminApi = getAdmin();

export const emailTypeKeys = {
  all: ["email-types"] as const,
  lists: () => [...emailTypeKeys.all, "list"] as const,
  list: (clientId: string) => [...emailTypeKeys.lists(), { clientId }] as const,
};

export type EmailType = IterableEmailTypeDTO;

export const useEmailTypes = (
  clientId: string,
  options?: Omit<UseQueryOptions<EmailType[], Error, EmailType[]>, "queryKey" | "queryFn">,
) =>
  useQuery({
    queryKey: emailTypeKeys.list(clientId),
    queryFn: () => emailTypeApi.getEmailTypes({ clientId }),
    staleTime: 5 * 60 * 1000,
    ...options,
    enabled: Boolean(clientId) && (options?.enabled ?? true),
  });

/**
 * Imperative loader for places that need email types outside React hooks
 * (effects, callbacks). Prefer `useEmailTypes` when possible.
 */
export const getEmailTypesRequest = (clientId: string): Promise<EmailType[]> =>
  emailTypeApi.getEmailTypes({ clientId });

export const useInvalidateEmailTypes = (clientId: string) => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: emailTypeKeys.list(clientId) });
};

export const useSetDefaultEmailType = (clientId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (emailTypeId: string) =>
      emailTypeApi.updateEmailType({ clientId, emailTypeId }, { defaultForClient: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailTypeKeys.list(clientId) });
    },
  });
};

export const useUpdateEmailTypeSenderDetails = (clientId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      emailTypeId,
      senders,
    }: {
      emailTypeId: string;
      senders: IterableEmailSenderDetailRequest[];
    }) => emailTypeApi.updateSenderDetails({ clientId, emailTypeId }, senders),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailTypeKeys.list(clientId) });
    },
  });
};

/**
 * Triggers a backend job that re-syncs Iterable email types into our DB.
 * Lives on the admin resource; the client-scoped `useEmailTypes` query is
 * invalidated here so a follow-up render reads the fresh data.
 */
export const useTriggerEmailTypeSync = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => adminApi.triggerIterableEmailTypeSync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailTypeKeys.all });
    },
  });
};
